import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiMicrophone,
	mdiMicrophoneOff,
	mdiVideo,
	mdiVideoOff,
	mdiPhoneHangup,
	mdiChat,
	mdiClose,
	mdiAccountHeart,
	mdiArrowLeft,
	mdiFullscreen,
	mdiFullscreenExit,
	mdiDoctor,
	mdiClockOutline,
	mdiContentCopy,
	mdiCheck,
	mdiSend,
	mdiTextBoxOutline,
	mdiPhoneInTalk,
	mdiAccountClock,
	mdiContentSave,
	mdiCheckCircle,
	mdiAlertCircleOutline,
	mdiFileDocumentOutline,
	mdiPill,
	mdiTestTube,
	mdiStethoscope,
	mdiChevronDown,
	mdiChevronUp,
} from "@mdi/js";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Tooltip,
	Paper,
	InputBase,
	Avatar,
	Chip,
	TextField,
	Badge,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Divider,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Alert,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { MainLayout, useMobileLayout } from "layouts";
import { navigate } from "shared/utils";
import { useAccount } from "shared/hooks/useAccount";
import { TeleconsultationService, QueueEntry, TranscriptionEntry } from "shared/services/TeleconsultationService";
import { MedDoctorServices } from "shared/services";
import styles from "./styles.module.scss";

// ── Timer ─────────────────────────────────────────────────────────

function useCallTimer(started: boolean) {
	const [seconds, setSeconds] = useState(0);
	useEffect(() => {
		if (!started) return;
		const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
		return () => clearInterval(interval);
	}, [started]);
	const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
	const ss = String(seconds % 60).padStart(2, "0");
	return `${mm}:${ss}`;
}

// ── Chat Message ──────────────────────────────────────────────────

interface ChatMsg {
	id: string;
	sender: string;
	isMe: boolean;
	text: string;
	time: string;
}

// ══════════════════════════════════════════════════════════════════
// PÁGINA DE LOBBY DO PACIENTE — Solicitar consulta
// ══════════════════════════════════════════════════════════════════

const PatientLobby: React.FC = () => {
	const account = useAccount();
	const [reason, setReason] = useState("");
	const [patientName, setPatientName] = useState(account?.displayName || "");
	const [waiting, setWaiting] = useState(false);
	const [roomId, setRoomId] = useState<string | null>(null);
	const [doctorName, setDoctorName] = useState<string | null>(null);
	const [position, setPosition] = useState<number | null>(null);

	useEffect(() => {
		setPatientName(account?.displayName || "");
	}, [account?.displayName]);

	// Ouve quando médico aceita
	useEffect(() => {
		const sub = TeleconsultationService.onConsultationAccepted((data) => {
			if (data.roomId === roomId) {
				setDoctorName(data.doctorName);
				// Redireciona para a sala de vídeo com appointmentId
				setTimeout(() => {
					const apptParam = data.appointmentId ? `&appointmentId=${data.appointmentId}` : "";
					navigate(`/medical/teleconsultation/${data.roomId}?role=patient${apptParam}`);
				}, 1500);
			}
		});
		return () => sub.stop();
	}, [roomId]);

	const handleRequest = () => {
		if (!patientName.trim()) return;
		const id = TeleconsultationService.requestConsultation(patientName.trim(), reason.trim() || "Consulta geral");
		setRoomId(id);
		setWaiting(true);
	};

	const handleCancel = () => {
		TeleconsultationService.cancelRequest();
		setWaiting(false);
		setRoomId(null);
		setPosition(null);
	};

	if (doctorName) {
		return (
			<MainLayout>
				<div className={styles.teleconsultationEnded}>
					<Icon path={mdiDoctor} size={3} color="var(--mui-palette-success-main)" />
					<h2>Médico encontrado!</h2>
					<p>Dr(a). <strong>{doctorName}</strong> aceitou sua consulta.</p>
					<p>Conectando à videochamada...</p>
					<CircularProgress size={28} sx={{ mt: 2 }} />
				</div>
			</MainLayout>
		);
	}

	if (waiting) {
		return (
			<MainLayout>
				<div className={styles.teleconsultationEnded}>
					<Icon path={mdiAccountClock} size={3} color="var(--mui-palette-warning-main)" />
					<h2>Aguardando médico disponível...</h2>
					<p>Você está na fila de espera. Um médico será notificado em breve.</p>
					{position && (
						<Chip label={`Posição na fila: ${position}`} variant="outlined" sx={{ mt: 1, mb: 2 }} />
					)}
					<CircularProgress size={28} sx={{ mt: 2, mb: 3 }} />
					<Button variant="outlined" color="error" onClick={handleCancel}>
						Cancelar solicitação
					</Button>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<div className={styles.teleconsultationEnded}>
				<Icon path={mdiPhoneInTalk} size={3} color="var(--mui-palette-primary-main)" />
				<h2>Solicitar Teleconsulta</h2>
				<p>Preencha seus dados para entrar na fila de atendimento.</p>

				<Box sx={{ width: "100%", maxWidth: 400, mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
					<TextField
						label="Seu nome"
						value={patientName}
						onChange={(e) => setPatientName(e.target.value)}
						fullWidth
						variant="outlined"
					/>
					<TextField
						label="Motivo da consulta (opcional)"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						fullWidth
						variant="outlined"
						multiline
						rows={3}
						placeholder="Descreva brevemente o motivo da consulta..."
					/>
					<Button
						variant="contained"
						size="large"
						startIcon={<Icon path={mdiPhoneInTalk} size={0.9} />}
						onClick={handleRequest}
						disabled={!patientName.trim()}
						sx={{ mt: 1 }}
					>
						Entrar na fila
					</Button>
					<Button variant="text" onClick={() => navigate("/medical/patient")} sx={{ mt: 1 }}>
						← Voltar
					</Button>
				</Box>
			</div>
		</MainLayout>
	);
};

// ══════════════════════════════════════════════════════════════════
// PÁGINA DE FILA DO MÉDICO — Ver pacientes aguardando
// ══════════════════════════════════════════════════════════════════

export const DoctorQueuePage: React.FC = () => {
	const account = useAccount();
	const [queue, setQueue] = useState<QueueEntry[]>([]);
	const [registered, setRegistered] = useState(false);
	const doctorName = account?.displayName || "Médico";

	// Registra como disponível
	useEffect(() => {
		if (!registered) return;
		TeleconsultationService.registerAsAvailable(doctorName);
		return () => {
			TeleconsultationService.unregisterAvailable();
		};
	}, [registered, doctorName]);

	// Ouve atualizações da fila
	useEffect(() => {
		if (!registered) return;
		const sub = TeleconsultationService.onQueueUpdate((q) => setQueue(q));
		return () => sub.stop();
	}, [registered]);

	const handleAccept = async (entry: QueueEntry) => {
		try {
			// Criar appointment com dados reais do paciente
			const appointment = await MedDoctorServices.createTeleconsultationAppointment(
				entry.patientId,
				entry.patientName,
				entry.reason || "Teleconsulta",
			);
			TeleconsultationService.acceptConsultation(entry.roomId, doctorName, appointment.id);
			navigate(`/medical/teleconsultation/${entry.roomId}?role=doctor&appointmentId=${appointment.id}&patientId=${entry.patientId}&patientName=${encodeURIComponent(entry.patientName)}`);
		} catch (err) {
			console.error("Erro ao criar consulta:", err);
			// Fallback: aceitar sem appointment
			TeleconsultationService.acceptConsultation(entry.roomId, doctorName);
			navigate(`/medical/teleconsultation/${entry.roomId}?role=doctor`);
		}
	};

	if (!registered) {
		return (
			<MainLayout>
				<div className={styles.teleconsultationEnded}>
					<Icon path={mdiDoctor} size={3} color="var(--mui-palette-primary-main)" />
					<h2>Plantão de Teleconsulta</h2>
					<p>Fique disponível para atender pacientes por videochamada.</p>
					<Button
						variant="contained"
						size="large"
						startIcon={<Icon path={mdiPhoneInTalk} size={0.9} />}
						onClick={() => setRegistered(true)}
						sx={{ mt: 3 }}
					>
						Ficar disponível
					</Button>
					<Button variant="text" onClick={() => navigate("/medical/doctor")} sx={{ mt: 1 }}>
						← Voltar
					</Button>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<div className={styles.doctorQueue}>
				<div className={styles.doctorQueueHeader}>
					<IconButton onClick={() => { setRegistered(false); navigate("/medical/doctor"); }}>
						<Icon path={mdiArrowLeft} size={1} />
					</IconButton>
					<h2>
						<Badge badgeContent={queue.length} color="error" sx={{ mr: 1 }}>
							<Icon path={mdiAccountClock} size={1.2} />
						</Badge>
						Fila de Espera
					</h2>
					<Chip
						label="● Online"
						size="small"
						sx={{ bgcolor: "rgba(76,175,80,0.15)", color: "#4CAF50", fontWeight: 600 }}
					/>
				</div>

				{queue.length === 0 ? (
					<div className={styles.doctorQueueEmpty}>
						<Icon path={mdiAccountClock} size={3} color="var(--mui-palette-text-disabled)" style={{ opacity: 0.3 }} />
						<p>Nenhum paciente na fila</p>
						<span>Aguarde — os pacientes aparecerão aqui automaticamente.</span>
						<CircularProgress size={20} sx={{ mt: 2, opacity: 0.5 }} />
					</div>
				) : (
					<div className={styles.doctorQueueList}>
						{queue.map((entry) => (
							<Paper key={entry.roomId} className={styles.doctorQueueCard} elevation={1}>
								<div className={styles.doctorQueueCardInfo}>
									<Avatar sx={{ bgcolor: "var(--mui-palette-primary-main)", width: 44, height: 44 }}>
										<Icon path={mdiAccountHeart} size={1.1} />
									</Avatar>
									<div>
										<strong>{entry.patientName}</strong>
										<span className={styles.doctorQueueReason}>{entry.reason}</span>
										<span className={styles.doctorQueueTime}>
											<Icon path={mdiClockOutline} size={0.55} />
											{new Date(entry.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
										</span>
									</div>
								</div>
								<Button
									variant="contained"
									color="success"
									startIcon={<Icon path={mdiPhoneInTalk} size={0.8} />}
									onClick={() => handleAccept(entry)}
								>
									Atender
								</Button>
							</Paper>
						))}
					</div>
				)}
			</div>
		</MainLayout>
	);
};

// ══════════════════════════════════════════════════════════════════
// PÁGINA DE VIDEOCHAMADA
// ══════════════════════════════════════════════════════════════════

const VideoCallPage: React.FC = () => {
	const { roomId } = useParams<{ roomId: string }>();
	const [searchParams] = useSearchParams();
	const role = (searchParams.get("role") as "doctor" | "patient") || "doctor";
	const account = useAccount();
	const { isMobile } = useMobileLayout();
	const myName = account?.displayName || (role === "doctor" ? "Médico" : "Paciente");

	// State
	const [status, setStatus] = useState<"loading" | "waiting" | "connected" | "ended">("loading");
	const [audioEnabled, setAudioEnabled] = useState(true);
	const [videoEnabled, setVideoEnabled] = useState(true);
	const [chatOpen, setChatOpen] = useState(false);
	const [transcriptionOpen, setTranscriptionOpen] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [copied, setCopied] = useState(false);

	// Chat
	const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
	const [chatInput, setChatInput] = useState("");

	// Transcrição
	const [transcription, setTranscription] = useState<TranscriptionEntry[]>([]);

	// Salvamento de transcrição
	const [transcriptionSaved, setTranscriptionSaved] = useState(false);
	const [savingTranscription, setSavingTranscription] = useState(false);

	// Análise clínica IA
	const [analyzingTranscription, setAnalyzingTranscription] = useState(false);
	const [clinicalAnalysis, setClinicalAnalysis] = useState<any>(null);
	const [analysisError, setAnalysisError] = useState<string | null>(null);
	const [savedAppointmentId, setSavedAppointmentId] = useState<string | null>(null);

	// Refs
	const localVideoRef = useRef<HTMLVideoElement>(null);
	const remoteVideoRef = useRef<HTMLVideoElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const wasConnectedRef = useRef(false);
	const mountedRef = useRef(false);
	const transcriptionEndRef = useRef<HTMLDivElement>(null);

	const callTime = useCallTimer(status === "connected");

	// ── Init ──────────────────────────────────────────────────────
	useEffect(() => {
		if (!roomId) return;
		if (mountedRef.current) return;
		mountedRef.current = true;

		let cancelled = false;
		wasConnectedRef.current = false;

		(async () => {
			try {
				const stream = await TeleconsultationService.getLocalMedia(true, true);
				if (localVideoRef.current) {
					localVideoRef.current.srcObject = stream;
				}

				await TeleconsultationService.joinRoom(
					roomId,
					(remoteStream) => {
						if (remoteVideoRef.current) {
							remoteVideoRef.current.srcObject = remoteStream;
						}
						if (!cancelled) {
							wasConnectedRef.current = true;
							setStatus("connected");
						}
					},
					(connectionState) => {
						if (cancelled) return;
						if (connectionState === "connected") {
							wasConnectedRef.current = true;
							setStatus("connected");
						}
						if ((connectionState === "disconnected" || connectionState === "failed") && wasConnectedRef.current) {
							setStatus("ended");
						}
					},
				);

				if (!cancelled) setStatus("waiting");
			} catch (err) {
				console.error("Erro ao iniciar teleconsulta:", err);
				if (!cancelled) setStatus("ended");
			}
		})();

		return () => { cancelled = true; };
	}, [roomId]);

	// Start transcription when connected
	useEffect(() => {
		if (status !== "connected") return;

		// Abre o painel de transcrição automaticamente
		setTranscriptionOpen(true);
		setChatOpen(false);

		TeleconsultationService.startTranscription(role, (entries) => {
			setTranscription(entries);
		});

		const sub = TeleconsultationService.onRemoteTranscription(() => {
			setTranscription(TeleconsultationService.getTranscription());
		});

		return () => { sub.stop(); };
	}, [status, role]);

	// Chat listener
	useEffect(() => {
		const sub = TeleconsultationService.onChatMessage((data) => {
			setChatMessages((prev) => [
				...prev,
				{ id: Date.now().toString(), sender: data.sender, isMe: false, text: data.message, time: data.time },
			]);
		});
		return () => sub.stop();
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			mountedRef.current = false;
			TeleconsultationService.hangUp();
		};
	}, []);

	// Auto-scroll transcription
	useEffect(() => {
		transcriptionEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [transcription]);

	// ── Controls ──────────────────────────────────────────────────
	const toggleMic = useCallback(() => {
		setAudioEnabled(TeleconsultationService.toggleAudio());
	}, []);

	const toggleCamera = useCallback(() => {
		setVideoEnabled(TeleconsultationService.toggleVideo());
	}, []);

	const handleHangUp = useCallback(() => {
		TeleconsultationService.hangUp();
		setStatus("ended");
	}, []);

	const toggleFullscreen = useCallback(() => {
		if (!containerRef.current) return;
		if (!document.fullscreenElement) {
			containerRef.current.requestFullscreen();
			setIsFullscreen(true);
		} else {
			document.exitFullscreen();
			setIsFullscreen(false);
		}
	}, []);

	const copyRoomLink = useCallback(() => {
		const link = `${window.location.origin}/medical/teleconsultation/${roomId}?role=patient`;
		navigator.clipboard.writeText(link);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [roomId]);

	const handleSendChat = useCallback(() => {
		if (!chatInput.trim() || !roomId) return;
		const time = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
		setChatMessages((prev) => [
			...prev,
			{ id: Date.now().toString(), sender: myName, isMe: true, text: chatInput.trim(), time },
		]);
		TeleconsultationService.sendChatMessage(roomId, chatInput.trim(), myName);
		setChatInput("");
	}, [chatInput, roomId, myName]);

	// ── Auto-save transcription when call ends ───────────────────
	const saveTranscriptionToBackend = useCallback(async (entries: TranscriptionEntry[]) => {
		if (entries.length === 0 || transcriptionSaved || savingTranscription) return;
		try {
			setSavingTranscription(true);

			const text = entries
				.map((e) => `[${new Date(e.timestamp).toLocaleTimeString("pt-BR")}] ${e.speaker === "doctor" ? "Médico" : "Paciente"}: ${e.text}`)
				.join("\n");

			// Tentar usar appointmentId da URL primeiro
			const urlAppointmentId = searchParams.get("appointmentId");
			if (urlAppointmentId) {
				await MedDoctorServices.saveTranscription(urlAppointmentId, text);
				setSavedAppointmentId(urlAppointmentId);
				setTranscriptionSaved(true);
				return;
			}

			// Tentar vincular a um appointment existente
			const appointments = await MedDoctorServices.listAppointments();
			if (appointments.length > 0) {
				const target = appointments
					.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime())
					.find(a => a.status === "scheduled" || a.status === "in_progress")
					|| appointments.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime())[0];
				if (target) {
					await MedDoctorServices.saveTranscription(target.id, text);
					setSavedAppointmentId(target.id);
					setTranscriptionSaved(true);
					return;
				}
			}

			// Se não existe appointment, criar um automaticamente para a teleconsulta
			const patientId = searchParams.get("patientId") || account?.uid || "";
			const patientName = searchParams.get("patientName") || "Paciente";
			const appointment = await MedDoctorServices.createTeleconsultationAppointment(
				patientId,
				patientName,
				"Teleconsulta",
			);
			await MedDoctorServices.saveTranscription(appointment.id, text);
			setSavedAppointmentId(appointment.id);
			setTranscriptionSaved(true);
		} catch (err) {
			console.error("Erro ao salvar transcrição:", err);
		} finally {
			setSavingTranscription(false);
		}
	}, [transcriptionSaved, savingTranscription, searchParams, account]);

	// ── Análise clínica da transcrição com IA ─────────────────────
	const handleAnalyzeTranscription = useCallback(async () => {
		const apptId = savedAppointmentId || searchParams.get("appointmentId");
		if (!apptId || analyzingTranscription) return;
		try {
			setAnalyzingTranscription(true);
			setAnalysisError(null);
			const record = await MedDoctorServices.analyzeTranscription(apptId);
			setClinicalAnalysis(record.structuredData);
		} catch (err: any) {
			console.error("Erro na análise:", err);
			setAnalysisError(err?.message || "Erro ao analisar transcrição");
		} finally {
			setAnalyzingTranscription(false);
		}
	}, [savedAppointmentId, searchParams, analyzingTranscription]);

	// Auto-save when status becomes ended (doctor only)
	useEffect(() => {
		if (status === "ended" && role === "doctor") {
			const entries = TeleconsultationService.getTranscription();
			saveTranscriptionToBackend(entries);
		}
	}, [status, role]);

	// ── Ended State ───────────────────────────────────────────────
	if (status === "ended") {
		const finalTranscription = TeleconsultationService.getTranscription();

		return (
			<MainLayout>
				<Box sx={{ p: 3, maxWidth: 900, mx: "auto", overflow: "auto", height: "100%" }}>
					{/* Header */}
					<Box sx={{ textAlign: "center", mb: 3 }}>
						<Icon path={mdiPhoneHangup} size={2.5} color="var(--mui-palette-error-main)" />
						<h2 style={{ margin: "8px 0 4px" }}>Teleconsulta Encerrada</h2>
						<Chip icon={<Icon path={mdiClockOutline} size={0.7} />} label={`Duração: ${callTime}`} variant="outlined" />
					</Box>

					{/* Status de salvamento */}
					{role === "doctor" && finalTranscription.length > 0 && (
						<Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
							<Chip
								icon={<Icon path={savingTranscription ? mdiClockOutline : transcriptionSaved ? mdiCheckCircle : mdiAlertCircleOutline} size={0.7} />}
								label={savingTranscription ? "Salvando transcrição..." : transcriptionSaved ? "Transcrição salva ✓ | Paciente cadastrado ✓" : "Erro ao salvar transcrição"}
								color={savingTranscription ? "default" : transcriptionSaved ? "success" : "warning"}
								variant="outlined"
							/>
						</Box>
					)}

					{/* Botão de Análise IA */}
					{role === "doctor" && transcriptionSaved && !clinicalAnalysis && (
						<Box sx={{ textAlign: "center", mb: 3 }}>
							<Button
								variant="contained"
								size="large"
								startIcon={analyzingTranscription ? <CircularProgress size={20} color="inherit" /> : <Icon path={mdiStethoscope} size={0.9} />}
								onClick={handleAnalyzeTranscription}
								disabled={analyzingTranscription}
								sx={{ borderRadius: 3, px: 4, py: 1.5, textTransform: "none", fontSize: "1rem" }}
							>
								{analyzingTranscription ? "Analisando com IA..." : "🤖 Gerar Prontuário + Receita + Exames"}
							</Button>
							{analysisError && (
								<Alert severity="error" sx={{ mt: 2, maxWidth: 500, mx: "auto" }}>{analysisError}</Alert>
							)}
						</Box>
					)}

					{/* Prontuário Clínico Estruturado */}
					{clinicalAnalysis && !clinicalAnalysis.parse_error && (
						<Paper elevation={2} sx={{ mb: 3, borderRadius: 3, overflow: "hidden" }}>
							<Box sx={{ bgcolor: "var(--mui-palette-primary-main)", color: "#fff", p: 2, display: "flex", alignItems: "center", gap: 1 }}>
								<Icon path={mdiFileDocumentOutline} size={1} />
								<Typography variant="h6" sx={{ fontWeight: 600 }}>História Clínica</Typography>
								<Chip label="Gerado por IA" size="small" sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }} />
							</Box>
							<Alert severity="info" sx={{ borderRadius: 0 }}>
								Este conteúdo foi gerado com IA e pode ter alguns erros. Verifique as informações.
							</Alert>

							{/* Identificação */}
							{clinicalAnalysis.identificacao && (
								<Accordion defaultExpanded={false}>
									<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
										<Typography sx={{ fontWeight: 600 }}>Identificação</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography><strong>Nome:</strong> {clinicalAnalysis.identificacao.nome}</Typography>
										<Typography><strong>Idade:</strong> {clinicalAnalysis.identificacao.idade}</Typography>
										<Typography><strong>Sexo:</strong> {clinicalAnalysis.identificacao.sexo}</Typography>
										<Typography><strong>Data:</strong> {clinicalAnalysis.identificacao.data_consulta}</Typography>
									</AccordionDetails>
								</Accordion>
							)}

							{/* Motivo da Consulta */}
							<Accordion defaultExpanded={false}>
								<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
									<Typography sx={{ fontWeight: 600 }}>Motivo de Consulta</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Typography>{clinicalAnalysis.motivo_consulta || "Não informado"}</Typography>
								</AccordionDetails>
							</Accordion>

							{/* Doença Atual */}
							<Accordion defaultExpanded={false}>
								<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
									<Typography sx={{ fontWeight: 600 }}>Doença Atual</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Typography sx={{ whiteSpace: "pre-wrap" }}>{clinicalAnalysis.enfermedad_actual || "Não informado"}</Typography>
								</AccordionDetails>
							</Accordion>

							{/* Revisão por Sistemas */}
							{clinicalAnalysis.revision_por_sistemas && (
								<Accordion defaultExpanded={false}>
									<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
										<Typography sx={{ fontWeight: 600 }}>Revisão por Sistemas</Typography>
									</AccordionSummary>
									<AccordionDetails>
										{Object.entries(clinicalAnalysis.revision_por_sistemas).map(([key, val]) => (
											<Typography key={key}><strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}:</strong> {val as string}</Typography>
										))}
									</AccordionDetails>
								</Accordion>
							)}

							{/* Antecedentes */}
							{clinicalAnalysis.antecedentes && (
								<Accordion defaultExpanded={false}>
									<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
										<Typography sx={{ fontWeight: 600 }}>Antecedentes</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography><strong>Patológicos:</strong> {clinicalAnalysis.antecedentes.patologicos}</Typography>
										<Typography><strong>Familiares:</strong> {clinicalAnalysis.antecedentes.familiares}</Typography>
										<Typography><strong>Alergias:</strong> {clinicalAnalysis.antecedentes.alergias?.join(", ") || "Nenhuma"}</Typography>
										<Typography><strong>Medicamentos:</strong> {clinicalAnalysis.antecedentes.medicamentos_em_uso?.join(", ") || "Nenhum"}</Typography>
										<Typography><strong>Hábitos:</strong> {clinicalAnalysis.antecedentes.habitos}</Typography>
									</AccordionDetails>
								</Accordion>
							)}

							{/* Sinais Vitais */}
							<Accordion defaultExpanded={false}>
								<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
									<Typography sx={{ fontWeight: 600 }}>Sinais Vitais</Typography>
								</AccordionSummary>
								<AccordionDetails>
									{clinicalAnalysis.signos_vitales ? Object.entries(clinicalAnalysis.signos_vitales)
										.filter(([, v]) => v && v !== "Não informado")
										.map(([k, v]) => (
											<Typography key={k}><strong>{k.replace(/_/g, " ")}:</strong> {v as string}</Typography>
										)) : <Typography>Não aferidos (teleconsulta)</Typography>}
								</AccordionDetails>
							</Accordion>

							{/* Exame Físico */}
							<Accordion defaultExpanded={false}>
								<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
									<Typography sx={{ fontWeight: 600 }}>Exame Físico</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Typography sx={{ whiteSpace: "pre-wrap" }}>{clinicalAnalysis.examen_fisico || "Limitado à teleconsulta"}</Typography>
								</AccordionDetails>
							</Accordion>

							{/* Resultados de Laboratório */}
							<Accordion defaultExpanded={false}>
								<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
									<Typography sx={{ fontWeight: 600 }}>Resultados de Laboratório e Provas Diagnósticas</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Typography sx={{ whiteSpace: "pre-wrap" }}>{clinicalAnalysis.resultados_laboratorio || "Sem resultados prévios"}</Typography>
								</AccordionDetails>
							</Accordion>

							{/* Análise */}
							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
									<Typography sx={{ fontWeight: 600 }}>Análise</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Typography sx={{ whiteSpace: "pre-wrap" }}>{clinicalAnalysis.analisis || "Não disponível"}</Typography>
								</AccordionDetails>
							</Accordion>

							{/* Plano */}
							{clinicalAnalysis.plan?.length > 0 && (
								<Accordion defaultExpanded>
									<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
										<Typography sx={{ fontWeight: 600 }}>Plano</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<List dense>
											{clinicalAnalysis.plan.map((item: any, i: number) => (
												<ListItem key={i}>
													<ListItemIcon sx={{ minWidth: 36 }}>
														<Icon path={mdiCheckCircle} size={0.8} color="var(--mui-palette-success-main)" />
													</ListItemIcon>
													<ListItemText
														primary={item.descricao}
														secondary={item.detalles}
													/>
												</ListItem>
											))}
										</List>
									</AccordionDetails>
								</Accordion>
							)}
						</Paper>
					)}

					{/* Receita Médica */}
					{clinicalAnalysis?.receita?.length > 0 && !clinicalAnalysis.parse_error && (
						<Paper elevation={2} sx={{ mb: 3, borderRadius: 3, overflow: "hidden" }}>
							<Box sx={{ bgcolor: "#e91e63", color: "#fff", p: 2, display: "flex", alignItems: "center", gap: 1 }}>
								<Icon path={mdiPill} size={1} />
								<Typography variant="h6" sx={{ fontWeight: 600 }}>Receita Médica</Typography>
							</Box>
							<Box sx={{ p: 2 }}>
								{clinicalAnalysis.receita.map((med: any, i: number) => (
									<Box key={i} sx={{ mb: 2, p: 2, bgcolor: "var(--mui-palette-action-hover)", borderRadius: 2 }}>
										<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{i + 1}. {med.medicamento}</Typography>
										<Typography variant="body2"><strong>Apresentação:</strong> {med.apresentacao}</Typography>
										<Typography variant="body2"><strong>Posologia:</strong> {med.posologia}</Typography>
										<Typography variant="body2"><strong>Duração:</strong> {med.duracao}</Typography>
										{med.observacoes && <Typography variant="body2" color="text.secondary"><strong>Obs:</strong> {med.observacoes}</Typography>}
									</Box>
								))}
							</Box>
						</Paper>
					)}

					{/* Exames Solicitados */}
					{clinicalAnalysis?.exames_solicitados?.length > 0 && !clinicalAnalysis.parse_error && (
						<Paper elevation={2} sx={{ mb: 3, borderRadius: 3, overflow: "hidden" }}>
							<Box sx={{ bgcolor: "#00897b", color: "#fff", p: 2, display: "flex", alignItems: "center", gap: 1 }}>
								<Icon path={mdiTestTube} size={1} />
								<Typography variant="h6" sx={{ fontWeight: 600 }}>Exames Solicitados</Typography>
							</Box>
							<List sx={{ p: 1 }}>
								{clinicalAnalysis.exames_solicitados.map((exam: any, i: number) => (
									<ListItem key={i} sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
										<ListItemIcon sx={{ minWidth: 36 }}>
											<Icon path={mdiCheckCircle} size={0.8} color="#00897b" />
										</ListItemIcon>
										<ListItemText
											primary={`${exam.exame} (${exam.urgencia})`}
											secondary={exam.justificativa}
										/>
									</ListItem>
								))}
							</List>
						</Paper>
					)}

					{/* Orientações ao Paciente */}
					{clinicalAnalysis?.orientacoes_paciente && !clinicalAnalysis.parse_error && (
						<Paper elevation={1} sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: "var(--mui-palette-info-main)", color: "#fff" }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>📋 Orientações ao Paciente</Typography>
							<Typography sx={{ whiteSpace: "pre-wrap" }}>{clinicalAnalysis.orientacoes_paciente}</Typography>
							{clinicalAnalysis.retorno && (
								<Typography sx={{ mt: 1, fontWeight: 600 }}>🔄 Retorno: {clinicalAnalysis.retorno}</Typography>
							)}
						</Paper>
					)}

					{/* Transcrição original (colapsada) */}
					{finalTranscription.length > 0 && (
						<Accordion sx={{ mb: 3, borderRadius: "12px !important", overflow: "hidden" }}>
							<AccordionSummary expandIcon={<Icon path={mdiChevronDown} size={1} />}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Icon path={mdiTextBoxOutline} size={0.9} />
									<Typography sx={{ fontWeight: 600 }}>Transcrição Original</Typography>
									<Chip label={`${finalTranscription.length} falas`} size="small" variant="outlined" />
								</Box>
							</AccordionSummary>
							<AccordionDetails sx={{ maxHeight: 300, overflow: "auto" }}>
								{finalTranscription.map((entry, i) => (
									<div key={i} style={{ marginBottom: 8 }}>
										<strong style={{ color: entry.speaker === "doctor" ? "#4CAF50" : "#2196F3" }}>
											{entry.speaker === "doctor" ? "🩺 Médico" : "👤 Paciente"}:
										</strong>{" "}
										<span>{entry.text}</span>
										<span style={{ fontSize: "0.75rem", color: "#999", marginLeft: 8 }}>
											{new Date(entry.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
										</span>
									</div>
								))}
							</AccordionDetails>
						</Accordion>
					)}

					{/* Ações */}
					<Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap", mb: 3 }}>
						<Button
							variant="contained"
							startIcon={<Icon path={mdiArrowLeft} size={0.8} />}
							onClick={() => navigate(role === "doctor" ? "/medical/doctor" : "/medical/patient")}
						>
							Voltar ao Painel
						</Button>
						{finalTranscription.length > 0 && (
							<Button
								variant="outlined"
								startIcon={<Icon path={mdiContentCopy} size={0.8} />}
								onClick={() => {
									const text = finalTranscription
										.map((e) => `[${new Date(e.timestamp).toLocaleTimeString("pt-BR")}] ${e.speaker === "doctor" ? "Médico" : "Paciente"}: ${e.text}`)
										.join("\n");
									navigator.clipboard.writeText(text);
								}}
							>
								Copiar transcrição
							</Button>
						)}
						{role === "doctor" && finalTranscription.length > 0 && !transcriptionSaved && !savingTranscription && (
							<Button
								variant="outlined"
								color="warning"
								startIcon={<Icon path={mdiContentSave} size={0.8} />}
								onClick={() => saveTranscriptionToBackend(finalTranscription)}
							>
								Tentar salvar novamente
							</Button>
						)}
					</Box>
				</Box>
			</MainLayout>
		);
	}

	// ── Side panel content ────────────────────────────────────────
	const sidePanel = chatOpen ? "chat" : transcriptionOpen ? "transcription" : null;

	return (
		<div className={styles.teleconsultation} ref={containerRef}>
			{/* ── Header ───────────────────────────── */}
			<div className={styles.teleconsultationHeader}>
				<div className={styles.teleconsultationHeaderLeft}>
					<IconButton size="small" onClick={handleHangUp} sx={{ color: "#fff" }}>
						<Icon path={mdiArrowLeft} size={1} />
					</IconButton>
					<div className={styles.teleconsultationHeaderInfo}>
						<span className={styles.teleconsultationTitle}>Teleconsulta</span>
						<span className={styles.teleconsultationStatus}>
							{status === "loading" && "Carregando..."}
							{status === "waiting" && "Aguardando conexão..."}
							{status === "connected" && (
								<>
									<span className={styles.teleconsultationLive} /> Em andamento • {callTime}
								</>
							)}
						</span>
					</div>
				</div>
				<div className={styles.teleconsultationHeaderRight}>
					{role === "doctor" && (
						<Tooltip title={copied ? "Link copiado!" : "Copiar link para paciente"}>
							<IconButton size="small" onClick={copyRoomLink} sx={{ color: "#fff" }}>
								<Icon path={copied ? mdiCheck : mdiContentCopy} size={0.9} />
							</IconButton>
						</Tooltip>
					)}
					<Tooltip title="Transcrição">
						<IconButton
							size="small"
							onClick={() => { setTranscriptionOpen(!transcriptionOpen); setChatOpen(false); }}
							sx={{ color: transcriptionOpen ? "#4CAF50" : "#fff" }}
						>
							<Icon path={mdiTextBoxOutline} size={0.9} />
						</IconButton>
					</Tooltip>
					<Tooltip title={isFullscreen ? "Sair da tela cheia" : "Tela cheia"}>
						<IconButton size="small" onClick={toggleFullscreen} sx={{ color: "#fff" }}>
							<Icon path={isFullscreen ? mdiFullscreenExit : mdiFullscreen} size={0.9} />
						</IconButton>
					</Tooltip>
					<Tooltip title={chatOpen ? "Fechar chat" : "Abrir chat"}>
						<IconButton
							size="small"
							onClick={() => { setChatOpen(!chatOpen); setTranscriptionOpen(false); }}
							sx={{ color: chatOpen ? "#2196F3" : "#fff" }}
						>
							<Icon path={chatOpen ? mdiClose : mdiChat} size={0.9} />
						</IconButton>
					</Tooltip>
				</div>
			</div>

			{/* ── Video Area ───────────────────────── */}
			<div className={`${styles.teleconsultationBody} ${sidePanel ? styles.chatVisible : ""}`}>
				<div className={styles.teleconsultationVideoArea}>
					{/* Remote Video */}
					<div className={styles.teleconsultationRemoteVideo}>
						<video ref={remoteVideoRef} autoPlay playsInline className={styles.teleconsultationVideo} />
						{status !== "connected" && (
							<div className={styles.teleconsultationVideoPlaceholder}>
								<Avatar sx={{ width: 100, height: 100, bgcolor: "rgba(255,255,255,0.15)", mb: 2 }}>
									<Icon path={role === "doctor" ? mdiAccountHeart : mdiDoctor} size={3} color="rgba(255,255,255,0.5)" />
								</Avatar>
								{status === "waiting" && (
									<>
										<CircularProgress size={24} sx={{ color: "#fff", mb: 1 }} />
										<p>Aguardando {role === "doctor" ? "paciente" : "médico"} conectar...</p>
									</>
								)}
								{status === "loading" && <CircularProgress size={32} sx={{ color: "#fff" }} />}
							</div>
						)}
					</div>

					{/* Local Video PiP */}
					<div className={styles.teleconsultationLocalVideo}>
						<video ref={localVideoRef} autoPlay playsInline muted className={styles.teleconsultationVideo} />
						{!videoEnabled && (
							<div className={styles.teleconsultationVideoOff}>
								<Icon path={mdiVideoOff} size={1.5} color="rgba(255,255,255,0.7)" />
							</div>
						)}
						<span className={styles.teleconsultationLocalLabel}>Você</span>
					</div>

					{/* Controls */}
					<div className={styles.teleconsultationControls}>
						<Tooltip title={audioEnabled ? "Desativar microfone" : "Ativar microfone"}>
							<IconButton
								className={`${styles.teleconsultationControlBtn} ${!audioEnabled ? styles.controlBtnOff : ""}`}
								onClick={toggleMic}
							>
								<Icon path={audioEnabled ? mdiMicrophone : mdiMicrophoneOff} size={1.1} />
							</IconButton>
						</Tooltip>
						<Tooltip title={videoEnabled ? "Desativar câmera" : "Ativar câmera"}>
							<IconButton
								className={`${styles.teleconsultationControlBtn} ${!videoEnabled ? styles.controlBtnOff : ""}`}
								onClick={toggleCamera}
							>
								<Icon path={videoEnabled ? mdiVideo : mdiVideoOff} size={1.1} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Encerrar chamada">
							<IconButton className={`${styles.teleconsultationControlBtn} ${styles.controlBtnHangup}`} onClick={handleHangUp}>
								<Icon path={mdiPhoneHangup} size={1.1} />
							</IconButton>
						</Tooltip>
					</div>
				</div>

				{/* ── Chat Sidebar ──────────────────────── */}
				{chatOpen && (
					<div className={styles.teleconsultationChat}>
						<div className={styles.teleconsultationChatHeader}>
							<h3>Chat da Consulta</h3>
							{isMobile && (
								<IconButton size="small" onClick={() => setChatOpen(false)}>
									<Icon path={mdiClose} size={0.8} />
								</IconButton>
							)}
						</div>
						<div className={styles.teleconsultationChatMessages}>
							{chatMessages.length === 0 && (
								<div className={styles.teleconsultationChatEmpty}>
									<Icon path={mdiChat} size={2} color="var(--mui-palette-text-disabled)" style={{ opacity: 0.3 }} />
									<p>Envie uma mensagem durante a consulta</p>
								</div>
							)}
							{chatMessages.map((msg) => (
								<div key={msg.id} className={`${styles.teleconsultationChatMsg} ${msg.isMe ? styles.chatMsgMe : styles.chatMsgPeer}`}>
									{!msg.isMe && <span className={styles.chatMsgSender}>{msg.sender}</span>}
									<span className={styles.chatMsgText}>{msg.text}</span>
									<span className={styles.chatMsgTime}>{msg.time}</span>
								</div>
							))}
						</div>
						<div className={styles.teleconsultationChatInput}>
							<Paper elevation={0} sx={{ display: "flex", flex: 1, borderRadius: "24px", border: "1px solid var(--mui-palette-divider)", px: 1.5 }}>
								<InputBase
									placeholder="Mensagem..."
									value={chatInput}
									onChange={(e) => setChatInput(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSendChat())}
									fullWidth
									sx={{ py: 0.8 }}
								/>
								<IconButton size="small" onClick={handleSendChat} disabled={!chatInput.trim()}>
									<Icon path={mdiSend} size={0.8} />
								</IconButton>
							</Paper>
						</div>
					</div>
				)}

				{/* ── Transcription Sidebar ─────────────── */}
				{transcriptionOpen && (
					<div className={styles.teleconsultationChat}>
						<div className={styles.teleconsultationChatHeader}>
							<h3>
								<Icon path={mdiTextBoxOutline} size={0.8} style={{ marginRight: 6, verticalAlign: "middle" }} />
								Transcrição
							</h3>
							{isMobile && (
								<IconButton size="small" onClick={() => setTranscriptionOpen(false)}>
									<Icon path={mdiClose} size={0.8} />
								</IconButton>
							)}
						</div>
						<div className={styles.teleconsultationChatMessages}>
							{transcription.length === 0 && (
								<div className={styles.teleconsultationChatEmpty}>
									<Icon path={mdiMicrophone} size={2} color="var(--mui-palette-text-disabled)" style={{ opacity: 0.3 }} />
									<p>A transcrição aparecerá aqui conforme vocês falam</p>
									<span style={{ fontSize: "0.8rem", opacity: 0.6 }}>Powered by Web Speech API</span>
								</div>
							)}
							{transcription.map((entry, i) => (
								<div key={i} className={`${styles.teleconsultationChatMsg} ${entry.speaker === role ? styles.chatMsgMe : styles.chatMsgPeer}`}>
									<span className={styles.chatMsgSender} style={{ color: entry.speaker === "doctor" ? "#4CAF50" : "#2196F3" }}>
										{entry.speaker === "doctor" ? "🩺 Médico" : "👤 Paciente"}
									</span>
									<span className={styles.chatMsgText}>{entry.text}</span>
									<span className={styles.chatMsgTime}>
										{new Date(entry.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
									</span>
								</div>
							))}
							<div ref={transcriptionEndRef} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

// ══════════════════════════════════════════════════════════════════
// ROTEAMENTO — decide qual view mostrar
// ══════════════════════════════════════════════════════════════════

export const TeleconsultationPage: React.FC = () => {
	const { roomId } = useParams<{ roomId: string }>();
	const account = useAccount();
	const profile = account?.settings?.medicalProfile;

	// Se tem roomId, é a videochamada (acessível a ambos)
	if (roomId && roomId !== "lobby" && roomId !== "queue") {
		return <VideoCallPage />;
	}

	// "queue" → fila do médico (apenas médicos)
	if (roomId === "queue") {
		if (profile !== "doctor" && profile !== "both") {
			return (
				<MainLayout>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2 }}>
						<Icon path={mdiDoctor} size={3} color="var(--mui-palette-text-disabled)" />
						<Box sx={{ fontSize: "1.25rem", fontWeight: 600 }}>Acesso restrito a médicos</Box>
						<Button variant="outlined" onClick={() => navigate("/medical")}>Voltar</Button>
					</Box>
				</MainLayout>
			);
		}
		return <DoctorQueuePage />;
	}

	// Sem roomId ou "lobby" → lobby do paciente (apenas pacientes)
	if (!roomId || roomId === "lobby") {
		if (profile !== "patient" && profile !== "both") {
			return (
				<MainLayout>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2 }}>
						<Icon path={mdiAccountHeart} size={3} color="var(--mui-palette-text-disabled)" />
						<Box sx={{ fontSize: "1.25rem", fontWeight: 600 }}>Acesso restrito a pacientes</Box>
						<Button variant="outlined" onClick={() => navigate("/medical")}>Voltar</Button>
					</Box>
				</MainLayout>
			);
		}
		return <PatientLobby />;
	}

	return <PatientLobby />;
};
